import clsx from 'clsx'
import { type MotionNodeDragHandlers, type MotionValue, motion } from 'framer-motion'
import { useCallback, useLayoutEffect, useRef } from 'react'
import { Height } from '../../primitives/index.ts'
import type { UpdateFunction } from '../hooks/useQueryState/useQueryState.ts'
import classes from './resize-navigation.module.css'

export type Props = {
  sidebarPosition: 'row' | 'row-reverse'
  mWidth: MotionValue<number>
  setNavigationWidth: UpdateFunction<number>
}

export function ResizeNavigation(props: Props) {
  const { sidebarPosition, mWidth, setNavigationWidth } = props

  const rSidebarPosition = useRef(sidebarPosition)
  useLayoutEffect(() => void (rSidebarPosition.current = sidebarPosition), [sidebarPosition])

  const handleOnResize = useCallback<Required<MotionNodeDragHandlers>['onDrag']>(
    (_event, info) => {
      const newWidth =
        rSidebarPosition.current === 'row-reverse' ? mWidth.get() - info.delta.x : mWidth.get() + info.delta.x

      if (newWidth > 250 && newWidth < 800) {
        mWidth.set(newWidth)
      }
    },
    [mWidth],
  )

  const handleOnResizeEnd = useCallback<Required<MotionNodeDragHandlers>['onDragEnd']>(
    (_event, _info) => {
      setNavigationWidth(mWidth.get())
    },
    [setNavigationWidth, mWidth],
  )

  const handleResetWidth = useCallback(() => {
    setNavigationWidth(350)
    mWidth.set(350)
  }, [setNavigationWidth, mWidth])

  const iconContainerClass = clsx(classes.iconContainer, {
    [classes.iconContainerRow]: sidebarPosition === 'row',
    [classes.iconContainerRowReverse]: sidebarPosition === 'row-reverse',
  })

  const dragHandleClass = clsx(classes.dragHandle, {
    [classes.dragHandleRow]: sidebarPosition === 'row',
    [classes.dragHandleRowReverse]: sidebarPosition === 'row-reverse',
  })

  const iconTooltipPosition = sidebarPosition === 'row' ? 'left' : 'right'

  return (
    <motion.div
      className={dragHandleClass}
      drag="x"
      dragConstraints={{ bottom: 0, left: 0, right: 0, top: 0 }}
      dragElastic={0}
      dragMomentum={false}
      onDrag={handleOnResize}
      onDragEnd={handleOnResizeEnd}
      whileDrag={{
        backgroundColor: 'rgb(230, 230, 230, 1)',
        transition: { duration: 0.1 },
      }}
      whileHover={{
        backgroundColor: 'rgb(230, 230, 230, 1)',
        transition: { duration: 0.1 },
      }}
    >
      <div
        className={iconContainerClass}
        data-testid="tidal-sandbox_resize_navigation"
        data-tooltip="Drag to resize - Double click to reset"
        data-tooltip-max-width="250"
        data-tooltip-position={iconTooltipPosition}
        onDoubleClick={handleResetWidth}
      >
        <Height className={classes.icon} />
      </div>
    </motion.div>
  )
}
